import { useEffect, useMemo, useState } from "react";
import {
  Add,
  CategoryOutlined,
  DnsOutlined,
  Search,
  SettingsEthernetOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import ItemCard from "./itemCard";
import ItemFormModal from "./itemFormModal";
import useStyles from "./styles";
import { mapItemToPayload, mapRowToItem, TIPO_CDN, TIPO_CLASSE } from "./utils";
import InternoModalConfirmacaoSaida from "../../../../../components/modalSaindoDaPagina";
import api from "../../../../../services/api";

export default function ConfigFlow() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [items, setItems] = useState([]);
  const [loadingItens, setLoadingItens] = useState(true);
  const [tipoAtivo, setTipoAtivo] = useState("cdn");
  const [busca, setBusca] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [modalItem, setModalItem] = useState(null);
  const [salvandoItem, setSalvandoItem] = useState(false);

  const [itemParaExcluir, setItemParaExcluir] = useState(null);
  const [excluindoItem, setExcluindoItem] = useState(false);

  useEffect(() => {
    fetchItens();
  }, []);

  const fetchItens = async () => {
    setLoadingItens(true);
    try {
      const response = await api.get("/interno/find/itensFlow");
      const mapeados = (response?.data || [])
        .filter((row) => row.tipo === TIPO_CDN || row.tipo === TIPO_CLASSE)
        .map(mapRowToItem);
      setItems(mapeados);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Erro ao buscar os itens do Flow",
        severity: "error",
      });
    } finally {
      setLoadingItens(false);
    }
  };

  // Cores pré-configuradas nos CDNs (mesmas usadas em 3.8-flow/2.5-flow),
  // resolvidas a partir do tema para respeitar o modo claro/escuro
  const coresDisponiveis = useMemo(
    () => [
      {
        key: "azul",
        label: "Azul",
        valor:
          theme.palette.mode === "dark"
            ? theme.palette.cardGraphic.graphColor.blueMedium
            : theme.palette.cardGraphic.graphColor.blue,
      },
      {
        key: "verde",
        label: "Verde",
        valor: theme.palette.cardGraphic.graphColor.green,
      },
      {
        key: "vermelho",
        label: "Vermelho",
        valor: theme.palette.cardGraphic.graphColor.red,
      },
      {
        key: "laranja",
        label: "Laranja",
        valor: theme.palette.cardGraphic.graphColor.orangeMedium,
      },
    ],
    [theme],
  );

  const coresMap = useMemo(
    () =>
      coresDisponiveis.reduce((acc, opcao) => {
        acc[opcao.key] = opcao;
        return acc;
      }, {}),
    [coresDisponiveis],
  );

  const cdns = useMemo(
    () => items.filter((item) => item.tipo === "cdn"),
    [items],
  );
  const classes = useMemo(
    () => items.filter((item) => item.tipo === "classe"),
    [items],
  );

  const itemsFiltrados = useMemo(() => {
    const base = tipoAtivo === "cdn" ? cdns : classes;
    const termo = busca.trim().toLowerCase();
    if (!termo) return base;
    return base.filter((item) =>
      item.identificador.toLowerCase().includes(termo),
    );
  }, [tipoAtivo, cdns, classes, busca]);

  const existingIdentificadores = useMemo(
    () =>
      items
        .filter((item) => item.tipo === tipoAtivo && item.id !== modalItem?.id)
        .map((item) => item.identificador.trim().toLowerCase()),
    [items, tipoAtivo, modalItem],
  );

  const handleAbrirCriar = () => {
    setModalMode("create");
    setModalItem(null);
    setModalOpen(true);
  };

  const handleAbrirEditar = (item) => {
    setModalMode("edit");
    setModalItem(item);
    setModalOpen(true);
  };

  const handleFecharModal = () => {
    if (salvandoItem) return;
    setModalOpen(false);
  };

  const handleSalvarItem = async (dados) => {
    setSalvandoItem(true);
    try {
      const payload = mapItemToPayload(dados);

      const response =
        modalMode === "edit" && modalItem
          ? await api.post("/interno/update/itensFlow", {
              id: modalItem.id,
              ...payload,
            })
          : await api.post("/interno/create/itensFlow", payload);

      const itemSalvo = mapRowToItem(response.data);

      setItems((prev) =>
        modalMode === "edit" && modalItem
          ? prev.map((item) => (item.id === modalItem.id ? itemSalvo : item))
          : [...prev, itemSalvo],
      );

      setSnackbar({
        children: `${dados.tipo === "cdn" ? "CDN" : "Classe"} ${
          modalMode === "edit" ? "atualizado" : "cadastrado"
        } com sucesso`,
        severity: "success",
      });
      setModalOpen(false);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children:
          error?.response?.data?.error || "Não foi possível salvar o item",
        severity: "error",
      });
    } finally {
      setSalvandoItem(false);
    }
  };

  const handleConfirmarExcluir = async () => {
    if (!itemParaExcluir) return;
    setExcluindoItem(true);
    try {
      await api.post("/interno/delete/itensFlow", { id: itemParaExcluir.id });
      setItems((prev) => prev.filter((item) => item.id !== itemParaExcluir.id));
      setSnackbar({
        children: "Item excluído com sucesso",
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      setSnackbar({
        children:
          error?.response?.data?.error || "Não foi possível excluir o item",
        severity: "error",
      });
    } finally {
      setExcluindoItem(false);
      setItemParaExcluir(null);
    }
  };

  return (
    <Box sx={styles.page}>
      <Paper
        sx={styles.mainPaper}
        elevation={theme.palette.mode === "dark" ? 0 : 3}
      >
        <Box sx={styles.header}>
          <Box sx={styles.headerTitleRow}>
            <SettingsEthernetOutlined color="primary" />
            <Typography variant="h5" sx={styles.title}>
              Configurações do Flow
            </Typography>
          </Box>
          <Typography sx={styles.subtitle}>
            Cadastre os CDNs e as classes de tráfego reconhecidos pelo EasyFlow.
            Cada item define a condição SQL usada para classificar o tráfego,
            além do ícone e da cor exibidos no dashboard.
          </Typography>
          <Alert severity="info" variant="outlined" sx={styles.infoAlert}>
            Os itens cadastrados aqui alimentam as opções disponíveis na
            configuração de visibilidade do Flow de cada cliente.
          </Alert>
        </Box>

        <Tabs
          value={tipoAtivo}
          onChange={(e, value) => {
            setTipoAtivo(value);
            setBusca("");
          }}
          sx={styles.tabs}
        >
          <Tab
            value="cdn"
            label={`CDNs (${cdns?.length})`}
            icon={<DnsOutlined fontSize="small" />}
            iconPosition="start"
            sx={styles.tab}
          />
          <Tab
            value="classe"
            label={`Classes de tráfego (${classes?.length})`}
            icon={<CategoryOutlined fontSize="small" />}
            iconPosition="start"
            sx={styles.tab}
          />
        </Tabs>

        <Box sx={styles.toolbar}>
          <TextField
            size="small"
            placeholder={
              tipoAtivo === "cdn" ? "Buscar CDN..." : "Buscar classe..."
            }
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            sx={styles.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAbrirCriar}
          >
            {tipoAtivo === "cdn" ? "Adicionar CDN" : "Adicionar classe"}
          </Button>
        </Box>

        {loadingItens ? (
          <Box sx={styles.loadingState}>
            <CircularProgress />
          </Box>
        ) : itemsFiltrados?.length === 0 ? (
          <Box sx={styles.emptyState}>
            <Typography sx={styles.emptyTitle}>
              {busca
                ? "Nenhum resultado encontrado"
                : tipoAtivo === "cdn"
                  ? "Nenhum CDN cadastrado"
                  : "Nenhuma classe cadastrada"}
            </Typography>
            <Typography sx={styles.emptyDescription}>
              {busca
                ? "Tente buscar por outro termo."
                : 'Clique em "Adicionar" para cadastrar o primeiro item.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.grid}>
            {itemsFiltrados?.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                corInfo={coresMap[item.cor]}
                onEditar={handleAbrirEditar}
                onExcluir={setItemParaExcluir}
              />
            ))}
          </Box>
        )}
      </Paper>

      <ItemFormModal
        open={modalOpen}
        mode={modalMode}
        tipo={tipoAtivo}
        initialData={modalMode === "edit" ? modalItem : null}
        coresDisponiveis={coresDisponiveis}
        existingIdentificadores={existingIdentificadores}
        loading={salvandoItem}
        onClose={handleFecharModal}
        onSave={handleSalvarItem}
      />

      <InternoModalConfirmacaoSaida
        openConfirmacao={Boolean(itemParaExcluir)}
        setOpenConfirmacao={(open) => {
          if (!open) setItemParaExcluir(null);
        }}
        confirmLoad={excluindoItem}
        execute={handleConfirmarExcluir}
        cancel={() => setItemParaExcluir(null)}
        severity="error"
        title="Excluir item"
        headerText={`Excluir "${itemParaExcluir?.identificador}"?`}
        contentText={
          itemParaExcluir?.tipo === "cdn"
            ? "O CDN será removido do catálogo e deixará de ser reconhecido na classificação de tráfego e na configuração de visibilidade dos clientes."
            : "A classe será removida do catálogo e deixará de ser reconhecida na classificação de tráfego."
        }
        confirmText="Excluir"
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
